import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { FaInfoCircle } from 'react-icons/fa';
import intlShape from '@/shared/utils/intlShape';
import Checkbox from '@/shared/components/input/Checkbox';
import handleHttpError from '@/shared/services/HandleHttpError';
import api from '../../../shared/api';
import config from '../../../config';
import { AddIcon, DeleteIcon, IconPencil } from '../../../shared/components/Icons';
import LoadingAnimation from '../../../shared/components/LoadingAnimation';
import EditableTextbox from '../../../shared/components/editable/EditableTextbox';
import YesNoModal from '../../../shared/components/YesNoModal';
import OkCancelModal from '../../../shared/components/OkCancelModal';
import ToastrService from '../../../shared/services/ToastrService';
import { injectStateService, StateService } from '../../../shared/services/StateService';
import { CanCreate, CanDelete, CanUpdate } from '../../../shared/components/security';
import authService, { privileges } from '../../../shared/services/AuthService';
import { getDocumentTypes } from '../Documents/documentTypesService';
import EditDocumentTagTypesDialog from './Modals/EditDocumentTagTypesDialog';
import EditDocumentTypesTagDialog from './Modals/EditDocumentTypesTagDialog';
import messages from './tagSettingsMessages';
import HasFeature from '@/shared/components/security/HasFeature';
import EditDocumentTagUserAccessDialog from './Modals/EditDocumentTagUserAccessDialog';
import ZendeskHelp from '@/shared/components/ZendeskHelp';

class DocumentTagConfiguration extends Component {
  state = {
    tags: null,
    tagToDelete: null,
    newTag: null,
    editTypesForTag: null,
    editTagsForType: null,
    loading: true,
    documentTypes: [],
  };

  componentDidMount() {
    const { intl } = this.props;
    api.base.get(`${config.appUrls.documents}/api/Tag/Config`).then(({ data: tags }) => {
      this.setState({
        tags,
        loading: false,
      });
    });
    getDocumentTypes(intl).then(documentTypes => {
      this.setState({
        documentTypes,
      });
    });
  }

  addTag = () => {
    const { newTag, tags } = this.state;
    const { intl } = this.props;
    if (!newTag) return;
    if (tags.find(x => x.name.toLowerCase() === newTag.toLowerCase())) {
      ToastrService.error(intl.formatMessage(messages.TagAlreadyExists));
      return;
    }

    api.base.post(`${config.appUrls.documents}/api/Tag`, { name: newTag }).then(({ data }) => {
      if (tags.find(x => x.id === data.id)) {
        // Tag already exists
        this.setState({
          newTag: null,
        });
      } else {
        this.setState(state => ({
          tags: [...state.tags, { documentCount: 0, ...data, documentTypes: [] }],
          newTag: null,
        }));
      }
    });
  };

  editTagName = (tag, value) =>
    api.base
      .put(`${config.appUrls.documents}/api/Tag`, { ...tag, name: value })
      .then(({ data }) => {
        const { name } = data;
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, name };
            }
            return t;
          }),
        }));
      });

  deleteTag = (tag, force = false) => {
    if (tag.documentCount === 0 || force) {
      api.base.delete(`${config.appUrls.documents}/api/Tag/${tag.id}`).then(() => {
        this.setState(state => ({
          tags: state.tags.filter(t => t.id !== tag.id),
          tagToDelete: null,
        }));
      });
    } else {
      this.setState({ tagToDelete: tag });
    }
  };

  updateTagTypes = newTypes => {
    const { editTypesForTag } = this.state;
    if (!editTypesForTag) return;
    if (newTypes) {
      this.setState(({ tags }) => ({
        tags: tags.map(x => {
          if (x.id !== editTypesForTag.id) return x;
          return { ...x, documentTypes: [...newTypes] };
        }),
        editTypesForTag: null,
      }));
    } else {
      // Cancel
      this.setState({
        editTypesForTag: null,
      });
    }
  };
  updateDocumentTypeTags = newTags => {
    const { editTagsForType } = this.state;
    if (!editTagsForType) return;
    if (newTags) {
      this.setState(({ tags }) => ({
        tags: tags.map(tag => {
          const documentTypesWithoutCurrent = (tag.documentTypes || []).filter(
            x => x !== editTagsForType.key,
          );
          if (newTags.findIndex(y => y.id === tag.id) >= 0) {
            return { ...tag, documentTypes: [...documentTypesWithoutCurrent, editTagsForType.key] };
          }

          return { ...tag, documentTypes: documentTypesWithoutCurrent };
        }),
        editTagsForType: null,
      }));
    } else {
      // Cancel
      this.setState({
        editTagsForType: null,
      });
    }
  };
  setTagIsPublicOnPortal = tag => {
    const { intl } = this.props;

    api.base
      .put(`${config.appUrls.documents}/api/Tag`, {
        ...tag,
        setIsPublicOnPortal: !tag.setIsPublicOnPortal,
      })
      .then(({ data }) => {
        const { setIsPublicOnPortal } = data;
        this.setState(state => ({
          tags: state.tags.map(t => {
            if (t.id === tag.id) {
              return { ...t, setIsPublicOnPortal };
            }
            return t;
          }),
        }));
        ToastrService.success(intl.formatMessage(messages.ChangesSaved));
      })
      .catch(handleHttpError(intl));
  };

  setDocumentTypeIsPublicOnPortal = documentType => {
    const { intl } = this.props;

    api.base
      .put(`${config.appUrls.documents}/api/DocumentTypeSetting`, {
        DocumentType: documentType.key,
        SetIsPublicOnPortal: !documentType.setIsPublicOnPortal,
      })
      .then(({ data }) => {
        const { setIsPublicOnPortal } = data;
        this.setState(state => ({
          documentTypes: state.documentTypes.map(d =>
            d.key === documentType.key ? { ...d, setIsPublicOnPortal } : d,
          ),
        }));
        ToastrService.success(intl.formatMessage(messages.ChangesSaved));
      })
      .catch(handleHttpError(intl));
  };

  render() {
    const {
      tags,
      tagToDelete,
      newTag,
      documentTypes,
      editTypesForTag,
      editTagsForType,
      loading,
      editTagAccess,
    } = this.state;
    const { intl, stateService, tab } = this.props;

    const tagLookup = (tags || []).reduce((prev, current) => {
      // eslint-disable-next-line no-param-reassign
      prev[current.id] = current.name;
      return prev;
    }, {});

    const tagsForType = (tags || []).reduce((prev, current) => {
      if (!current.documentTypes) return prev;
      current.documentTypes.forEach(documentType => {
        if (!prev[documentType]) prev[documentType] = [];
        prev[documentType].push(current);
      });
      return prev;
    }, {});

    const typeLookup = (documentTypes || []).reduce((result, item) => {
      // eslint-disable-next-line no-param-reassign
      result[item.key] = item.label;
      return result;
    }, {});
    const canUpdateTags = authService.hasPermission({
      functionality: 'Tags',
      privilege: privileges.Update,
    });
    const canUpdateSettings = authService.hasPermission({
      functionality: 'Settings',
      privilege: privileges.Update,
    });

    return (
      <>
        <div className="panel-heading">
          <CanCreate on="Tags">
            <div className="panel-buttons">
              <button
                type="button"
                className="btn btn-inverse"
                onClick={() => this.setState({ newTag: '' })}
              >
                <i className="fa fa-plus" /> <FormattedMessage {...messages.AddTag} />
              </button>
            </div>
          </CanCreate>
        </div>
        {tab === 0 && !loading && tags ? (
          <div className="panel-body">
            <table className="table table-grid document-tag-grid">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage {...messages.Name} />
                  </th>
                  <th>
                    <FormattedMessage {...messages.ApplyToDocumentTypes} />
                  </th>
                  <th className="text-right">
                    <FormattedMessage {...messages.NrOfDocuments} />
                  </th>
                  <HasFeature feature="DocumentTagUserAccessRestriction">
                    <th className="text-right">
                      <FormattedMessage {...messages.AllowedUsers} />
                      <ZendeskHelp article="19596660717596" />
                    </th>
                  </HasFeature>
                  <th className="text-right">
                    <FormattedMessage {...messages.SetIsPublicOnPortal} />
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement="bottom"
                      rootClose
                      overlay={
                        <Popover id="popover-trigger-hover-focus">
                          {intl.formatMessage(messages.SetIsPublicOnPortalExplanation)}
                        </Popover>
                      }
                    >
                      <span className="margin10L explanation-icon">
                        <FaInfoCircle />
                      </span>
                    </OverlayTrigger>
                  </th>
                  <th className="text-right">
                    <FormattedMessage {...messages.Actions} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {tags.map(tag => (
                  <tr key={tag.id}>
                    <td>
                      <EditableTextbox
                        value={tag.name}
                        onChange={value => this.editTagName(tag, value)}
                        maxLength="200"
                        readOnly={!canUpdateTags}
                      />
                    </td>
                    <td>
                      <CanUpdate
                        on="Settings"
                        fallback={
                          tag.documentTypes && tag.documentTypes.length > 0
                            ? tag.documentTypes.map(type => typeLookup[type]).join(', ')
                            : null
                        }
                      >
                        <a onClick={() => this.setState({ editTypesForTag: tag })}>
                          {tag.documentTypes && tag.documentTypes.length > 0 ? (
                            tag.documentTypes.map(type => typeLookup[type]).join(', ')
                          ) : (
                            <IconPencil />
                          )}
                        </a>
                      </CanUpdate>
                    </td>
                    <td className="text-right">
                      {tag.documentCount === 0 ? (
                        0
                      ) : (
                        <a
                          style={{ display: 'block' }}
                          onClick={() => stateService.gotoDocuments({ tags: tag.id })}
                        >
                          {tag.documentCount}
                        </a>
                      )}
                    </td>
                    <HasFeature feature="DocumentTagUserAccessRestriction">
                      <td className="text-right bold text-nowrap">
                        <a onClick={() => this.setState({ editTagAccess: tag })}>
                          {!tag.userAccessCount || tag.userAccessCount === 0 ? (
                            <FormattedMessage {...messages.Everyone} />
                          ) : tag.userAccessCount === 1 ? (
                            <FormattedMessage {...messages.SingleAllowedUser} />
                          ) : (
                            intl.formatMessage(messages.AllowedUserCount, {
                              count: tag.userAccessCount,
                            })
                          )}
                        </a>
                      </td>
                    </HasFeature>
                    <td className="text-right">
                      <Checkbox
                        onClick={() => this.setTagIsPublicOnPortal(tag)}
                        value={tag.setIsPublicOnPortal}
                        disabled={!canUpdateSettings}
                      />
                    </td>
                    <td className="text-right">
                      <CanDelete on="Tags">
                        <a onClick={() => this.deleteTag(tag)}>
                          <DeleteIcon />
                        </a>
                      </CanDelete>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <YesNoModal
              show={!!tagToDelete}
              title={intl.formatMessage(messages.DeleteDocumentTag)}
              message={intl.formatMessage(messages.DeleteDocumentTagConfirmationMsg)}
              onNo={() => this.setState({ tagToDelete: null })}
              onSave={() => this.deleteTag(tagToDelete, true)}
            />
            {!!editTypesForTag && (
              <EditDocumentTagTypesDialog
                tagId={editTypesForTag.id}
                tagName={editTypesForTag.name}
                types={editTypesForTag.documentTypes}
                onClose={this.updateTagTypes}
              />
            )}
            {!!editTagAccess && (
              <EditDocumentTagUserAccessDialog
                tagId={editTagAccess.id}
                onClose={userAccesses =>
                  this.setState({
                    editTagAccess: undefined,
                    tags: tags.map(x => {
                      if (x.id === editTagAccess.id) {
                        return { ...x, userAccessCount: userAccesses?.length };
                      }
                      return x;
                    }),
                  })
                }
              />
            )}
          </div>
        ) : null}
        {tab === 1 && !loading && documentTypes ? (
          <div className="panel-body">
            <table className="table table-grid document-tag-grid">
              <thead>
                <tr>
                  <th>
                    <FormattedMessage {...messages.Name} />
                  </th>
                  <th>
                    <FormattedMessage {...messages.DocumentTags} />
                  </th>
                  <th className="text-right">
                    <FormattedMessage {...messages.SetIsPublicOnPortal} />
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement="bottom"
                      rootClose
                      overlay={
                        <Popover id="popover-trigger-hover-focus">
                          {intl.formatMessage(messages.SetIsPublicOnPortalExplanation)}
                        </Popover>
                      }
                    >
                      <span className="margin10L explanation-icon">
                        <FaInfoCircle />
                      </span>
                    </OverlayTrigger>
                  </th>
                </tr>
              </thead>
              <tbody>
                {!!tagsForType &&
                  documentTypes.map(documentType => (
                    <tr key={documentType.key}>
                      <td>
                        <label>{documentType.label}</label>
                      </td>
                      <td>
                        <CanUpdate
                          on="Settings"
                          fallback={
                            tagsForType[documentType.key] &&
                            tagsForType[documentType.key].length > 0
                              ? tagsForType[documentType.key]
                                  .map(tag => tagLookup[tag.id])
                                  .join(', ')
                              : null
                          }
                        >
                          <a
                            onClick={() =>
                              this.setState({
                                editTagsForType: {
                                  ...documentType,
                                  tags: tagsForType[documentType.key] || [],
                                },
                              })
                            }
                          >
                            {tagsForType[documentType.key] &&
                            tagsForType[documentType.key].length > 0 ? (
                              tagsForType[documentType.key].map(tag => tagLookup[tag.id]).join(', ')
                            ) : (
                              <IconPencil />
                            )}
                          </a>
                        </CanUpdate>
                      </td>
                      <td className="text-right">
                        <Checkbox
                          onClick={() => this.setDocumentTypeIsPublicOnPortal(documentType)}
                          value={documentType.setIsPublicOnPortal}
                          disabled={!canUpdateSettings}
                        />
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {!!editTagsForType && (
              <EditDocumentTypesTagDialog
                documentTypeId={editTagsForType.key}
                documentTypeName={editTagsForType.label}
                documentTags={editTagsForType.tags}
                allTags={tags}
                onClose={this.updateDocumentTypeTags}
              />
            )}
          </div>
        ) : null}

        <OkCancelModal
          show={newTag !== null}
          title={intl.formatMessage(messages.AddTag)}
          onClose={() => this.setState({ newTag: null })}
          onSave={() => this.addTag()}
        >
          {newTag !== null && (
            <input
              type="text"
              className="form-control"
              placeholder={intl.formatMessage(messages.EnterTag)}
              required
              value={newTag}
              onChange={e => this.setState({ newTag: e.target.value })}
              onKeyDown={e => {
                if (e.keyCode === 13) {
                  this.addTag();
                } else if (e.keyCode === 27) {
                  this.setState({ newTag: null });
                }
              }}
              autoFocus
            />
          )}
        </OkCancelModal>
        {loading && <LoadingAnimation />}
      </>
    );
  }
}

DocumentTagConfiguration.propTypes = {
  intl: intlShape.isRequired,
  stateService: PropTypes.instanceOf(StateService).isRequired,
  tab: PropTypes.number.isRequired,
};

export default injectStateService(injectIntl(DocumentTagConfiguration));
